/**
 * DNAVerificationCard
 *
 * Owner-side surface for the $75 DNA-Verified Pedigree Registration
 * (Pricing V2 Phase 2). Renders four discrete states:
 *
 *   1. No purchase yet  → "Submit for DNA Verification ($75)" CTA
 *   2. pending_upload   → file picker (PDF / image) to attach the panel
 *   3. pending_review   → "Submitted — under review" + can add more files
 *   4. approved         → gold DNA-Verified badge + confirmation copy
 *   5. rejected         → reject reason + "Submit again" path
 *
 * Read-only for non-owners. The badge surface on the page header is
 * handled separately by `<DNAVerifiedBadge />`.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DNAVerifiedBadge from "@/components/DNAVerifiedBadge";
import { TestTubeIcon as TestTube, CloudArrowUpIcon as CloudArrowUp, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowCounterClockwiseIcon as ArrowCounterClockwise } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/utils-app";
import PromoCodeInput from "@/components/PromoCodeInput";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";
import CompBanner from "@/components/CompBanner";

export default function DNAVerificationCard({ dog, isOwner, onDogPatched }) {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [isElite, setIsElite] = useState(false);
  const [promo, setPromo] = useState(null);
  const fileRef = useRef(null);

  const dogId = dog?.id;

  const load = async () => {
    if (!dogId || !isOwner) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/dna-verifications/mine");
      const match = (res.data || []).find((r) => r.dog_id === dogId);
      setVerification(match || null);
    } catch {
      // ignore — owner just hasn't bought it yet
    } finally {
      setLoading(false);
    }
  };

  // Detect Kennel Elite — if true, DNA verification is free and the
  // pitch card flips to "Included with Kennel Elite" copy.
  useEffect(() => {
    if (!isOwner) return;
    api.get("/billing/my-subscription", { silent: true })
      .then((r) => {
        const planKey = r.data?.subscription?.plan;
        setIsElite(planKey === "kennel_elite");
      })
      .catch(() => setIsElite(false));
  }, [isOwner]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dogId, isOwner]);

  const buy = async () => {
    setBusy(true);
    try {
      const res = await api.post("/billing/checkout", {
        package_id: "dna_pedigree_verification",
        dog_id: dogId,
        origin_url: webOrigin(),
        promo_code: promo?.valid ? promo.code : undefined,
      });
      if (res.data?.free) {
        toast.success("DNA verification opened — upload your panel below.");
        setVerification(res.data.verification);
      } else if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file || !verification?.id) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", "");
      const res = await api.post(
        `/dna-verifications/${verification.id}/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("DNA panel uploaded — under review.");
      // Reload to pick up the new file + status flip.
      await load();
      // Reset the input so the same file can be re-selected after a fix.
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  // Public surface — non-owners just see the badge if dog is approved.
  if (!isOwner) {
    if (!dog?.dna_verified) return null;
    return (
      <div
        className="bg-gradient-to-br from-[#FBF7EC] to-white border border-[#C9A227]/30 rounded-xl p-5 flex items-start gap-4"
        data-testid="dna-card-public-approved"
      >
        <TestTube size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-spectral text-lg">DNA-Verified pedigree</h3>
            <DNAVerifiedBadge verified size="sm" showLabel={false} />
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Parentage on this dog has been confirmed by our admin team
            against a third-party DNA panel report (Embark / Wisdom Panel / VGL).
          </p>
        </div>
      </div>
    );
  }

  // Approved — celebratory state.
  if (dog?.dna_verified || verification?.status === "approved") {
    return (
      <div
        className="bg-gradient-to-br from-[#FBF7EC] to-white border border-[#C9A227]/40 rounded-xl p-5"
        data-testid="dna-card-approved"
      >
        <div className="flex items-start gap-4">
          <TestTube size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-spectral text-lg">DNA-Verified pedigree</h3>
              <DNAVerifiedBadge verified size="sm" showLabel={false} />
            </div>
            <p className="text-sm text-[#6B6B6B]">
              The gold DNA-Verified badge now appears on this dog's marketplace
              listings, your kennel page, and the pedigree share card.
            </p>
            {verification?.reviewed_at && (
              <p className="text-xs text-[#6B6B6B] mt-2">
                Approved {verification.reviewed_at.slice(0, 10)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not yet purchased — pitch the SKU. Kennel Elite subscribers get
  // it FREE (the /billing/checkout endpoint returns {free:true} for
  // them and we route straight into the upload step), so we flip the
  // headline + button copy + drop the price.
  if (!verification) {
    return (
      <div
        className="bg-white border border-[#EAEAEA] rounded-xl p-5"
        data-testid="dna-card-pitch"
      >
        <div className="flex items-start gap-4">
          <TestTube size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h3 className="font-spectral text-lg">
                {isElite ? (isNativeApp() ? "DNA-Verified pedigree — included with your plan" : "DNA-Verified pedigree — included with Kennel Elite") : "Add DNA-Verified to this pedigree"}
              </h3>
              {isElite ? (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider bg-[#FBF7EC] text-[#8A6A00] border border-[#E8D9A6] rounded-full px-2 py-0.5 whitespace-nowrap"
                  data-testid="dna-card-elite-badge"
                >
                  ✓ {isNativeApp() ? "Included with your plan" : "Free with Kennel Elite"}
                </span>
              ) : (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider bg-[#E0F2FE] text-[#075985] border border-[#BAE6FD] rounded-full px-2 py-0.5 whitespace-nowrap"
                  data-testid="dna-card-sla-badge"
                >
                  ✓ Reviewed within 2 business days
                </span>
              )}
            </div>
            <p className="text-sm text-[#6B6B6B] mt-2">
              Submit your dog's DNA panel report (Embark, Wisdom Panel, VGL, or equivalent).
              On approval, the gold DNA-Verified badge appears on this dog across every
              listing and your kennel page.
              {isElite ? (
                <span className="font-semibold text-ink"> No charge — every Kennel Elite subscription includes unlimited DNA-Verified Registrations.</span>
              ) : (
                <span className="font-semibold text-ink"> One-time $75.</span>
              )}
            </p>
            <p className="text-xs text-[#6B6B6B] mt-2 italic">
              Note: this is an admin review of your third-party DNA panel — Armstrong K9 Registry
              does not lab-test independently.
            </p>
            {!isElite && (
              <div className="mt-3">
                <CompBanner className="mb-3" />
                <PromoCodeInput
                  kind="dna_pedigree_verification"
                  amount={75}
                  applied={promo}
                  onApplied={(p) => {
                    setPromo(p);
                    toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
                  }}
                  onCleared={() => setPromo(null)}
                />
              </div>
            )}
            {isIOS() && !isElite && (
              <IosPurchaseNotice label="DNA-Verified Registration" showPlans={false} className="mt-3" />
            )}
            <Button
              onClick={buy}
              disabled={busy}
              className={`mt-3 bg-ink hover:bg-black text-white ${isIOS() && !isElite ? "hidden" : ""}`}
              data-testid="dna-card-buy-btn"
            >
              {busy
                ? "Working…"
                : isElite
                ? "Submit for DNA Verification (free)"
                : promo?.valid
                ? `Submit for DNA Verification — $${promo.final.toFixed(2)}`
                : "Submit for DNA Verification — $75"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // pending_upload / pending_review / rejected — file upload area.
  const showRejection = verification.status === "rejected" || verification.reject_reason;
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-5"
      data-testid="dna-card-pending"
    >
      <div className="flex items-start gap-4">
        <TestTube size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h3 className="font-spectral text-lg">DNA Verification — in progress</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#075985] bg-[#E0F2FE] border border-[#BAE6FD] rounded-full px-2 py-0.5">
              {verification.status.replace("_", " ")}
            </span>
          </div>

          {showRejection && verification.reject_reason && (
            <div
              className="mt-3 border-l-2 border-crimson bg-[#FEF2F2] text-[#7F1D1D] text-sm p-3 rounded-r-lg"
              data-testid="dna-card-reject-reason"
            >
              <div className="flex items-center gap-2 mb-1">
                <Warning size={16} weight="fill" />
                <span className="font-semibold">Reviewer requested changes</span>
              </div>
              <p>{verification.reject_reason}</p>
            </div>
          )}

          {(verification.files || []).length === 0 ? (
            <p className="text-sm text-[#6B6B6B] mt-2">
              Upload your DNA panel report (PDF or image, max 8MB). Most labs (Embark, Wisdom Panel,
              VGL) provide a downloadable parentage panel PDF.
            </p>
          ) : (
            <div className="mt-3 space-y-1.5">
              <div className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">
                Uploaded files ({verification.files.length})
              </div>
              <ul className="text-sm space-y-1">
                {verification.files.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-2 text-[#6B6B6B]"
                    data-testid={`dna-uploaded-file-${f.id}`}
                  >
                    <CheckCircle size={14} weight="fill" className="text-green-700" />
                    <span className="truncate flex-1">{f.filename}</span>
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-crimson hover:underline"
                    >
                      Open ↗
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              onChange={(e) => upload(e.target.files?.[0])}
              className="text-sm block w-full max-w-full min-w-0"
              disabled={busy}
              data-testid="dna-card-file-input"
            />
            {(verification.files || []).length > 0 && (
              <span className="text-xs text-[#6B6B6B] inline-flex items-center gap-1">
                <ArrowCounterClockwise size={12} />
                Add another file
              </span>
            )}
          </div>

          {verification.status === "pending_review" && (
            <p className="text-xs text-[#6B6B6B] mt-3 italic flex items-center gap-1.5">
              <CloudArrowUp size={14} /> Our reviewers see your submission in their queue. We'll
              email you when a decision is made.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
